import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { App } from "./App";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { initTheme } from "@/lib/theme-store";
import "./styles/index.css";

// Before the first render, so a user on forced-dark never sees a white flash.
initTheme();

/**
 * Recover from a lazily-loaded chunk that fails to arrive.
 *
 * Two ways this happens: the user is on an index.html from a previous deploy
 * whose chunks are gone, or an edge served the SPA fallback in place of the
 * script so the browser refused to execute it. Either way the route renders
 * nothing and a plain reload fixes it, because index.html is never cached.
 *
 * Reload once only — a sessionStorage flag keeps a genuinely broken build from
 * turning into a refresh loop.
 */
const CHUNK_RELOAD_KEY = "dyuthi-chunk-reload";
window.addEventListener("error", (event) => {
  const target = event.target as HTMLElement | null;
  const isScriptOrStyle =
    target instanceof HTMLScriptElement || target instanceof HTMLLinkElement;
  if (!isScriptOrStyle) return;
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
  window.location.reload();
}, true); // capture — resource errors do not bubble

// A clean render means the assets resolved; let a future failure retry.
window.addEventListener("load", () => {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
});

// Register the offline shell after load, so fetching the worker never competes
// with the assets needed for first paint. Failure is non-fatal by design: the
// app works fine without it, it just won't open offline.
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    // `updateViaCache: "none"` makes the browser fetch sw.js past the HTTP
    // cache on every update check. Without it the worker's own Cache-Control
    // decides how long a stale worker survives a deploy, and on this zone that
    // header is being rewritten to four hours somewhere above Pages — the
    // deployment URL returns max-age=0 while the custom domain does not.
    navigator.serviceWorker
      .register("/sw.js", { updateViaCache: "none" })
      .catch(() => {
        /* unsupported browser, private mode, or blocked — nothing to do */
      });
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
